import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IHttpPagedResponse } from '../../../utils/models/http-paged-response.model';
import { IService } from '../../models/services-list.model';
import { IFeatureService } from '../../models/featured-service.model';
import { IEnvironment } from '../../models/environment.model';
import { IHttpUnpagedResponse } from '../../../utils/models/http-unpaged-response.model';
import { IApplicationDetails } from '../../models/application-details.model';
import { IResponse } from '../../models/response.model';
import { IServiceTranslation } from '../../models/service-translation.model';
import { apis } from '../../../../app-apis';
import { map, Observable } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IServiceData } from '../../models/layout.model';

@Injectable({
  providedIn: 'root',
})
export class PublicServicesService {
  private environment: IEnvironment;

  constructor(
    private http: HttpClient,
    @Inject('environment') environment: IEnvironment
  ) {
    this.environment = environment;
  }

  getServices(queryParams: string, servicesBaseUrl: string) {
    return this.http.get<IHttpPagedResponse<IService>>(
      `${servicesBaseUrl}/service/api/v1/public/services?${queryParams}`
    );
  }

  agentGetServices(queryParams: string, servicesBaseUrl: string) {
    return this.http.get<IHttpPagedResponse<IService>>(
      `${servicesBaseUrl}/service/api/v1/agent/services?${queryParams}`
    );
  }

  getFeaturedServices(servicesBaseUrl: string) {
    return this.http.get<IHttpPagedResponse<IFeatureService>>(
      `${servicesBaseUrl}/service/api/v1/public/featured-services`
    );
  }

  getApplicationByParentID(parentApplicationId: string) {
    return this.http.get<IHttpUnpagedResponse<IApplicationDetails>>(
      `${this.environment.apiGatewayBaseUrl}/application/v1/applications/by-parent-id?parentId=${parentApplicationId}`
    );
  }

  downloadAttachment(applicationId: string, fileName: string) {
    return this.http.get<Blob>(
      `${this.environment.apiGatewayBaseUrl}/application/v1/file-manager/application/attachment`,
      {
        params: { id: applicationId, fileName },
        responseType: 'blob' as 'json',
        observe: 'response',
      }
    );
  }

  private setDefaultLocaleValue(response: Record<string, unknown>) {
    const newObject: Record<string, unknown> = {};

    Object.entries(response).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        newObject[key] = this.setDefaultLocaleValue(
          value as Record<string, unknown>
        );
      } else {
        newObject[key] =
          typeof value === 'string' && value === '' ? key : value;
      }
    });

    return newObject as unknown as IResponse<IServiceTranslation>;
  }

  getServiceTranslationsByLocale(
    serviceId: string,
    locale: string,
    servicesBaseUrl: string
  ) {
    return this.http
      .get<IResponse<IServiceTranslation>>(
        `${this.environment.apiGatewayBaseUrl}/service${apis.service.public}/${serviceId}/translations/${locale}`
      )
      .pipe(
        map(res =>
          this.setDefaultLocaleValue(res as unknown as Record<string, unknown>)
        )
      );
  }

  getJourneyServiceMapping(
    changeRequestId: string
  ): Observable<IResponse<Record<string, unknown>>> {
    return this.http.get<IResponse<Record<string, unknown>>>(
      `${this.environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/journey-mapping`
    );
  }

  getServiceByCode(serviceCode: string): Observable<IService> {
    return this.http
      .get<IResponse<IService>>(
        `${this.environment.apiGatewayBaseUrl}/service${apis.service.public}/code/${serviceCode}`
      )
      .pipe(
        map(res => {
          return res.data;
        })
      );
  }

  getServicesTranslations(serviceIds: string[], locale: string) {
    return this.http
      .get<IResponse<IServiceTranslation>>(
        `${this.environment.apiGatewayBaseUrl}/service${apis.service.public}/translations/${locale}`,
        {
          headers: {
            serviceIds: serviceIds.join(','),
          },
        }
      )
      .pipe(
        map(res =>
          this.setDefaultLocaleValue(res as unknown as Record<string, unknown>)
        )
      );
  }

  getChangeRequestApplicationForm(
    changeRequestId: string
  ): Observable<FormlyFieldConfig[]> {
    return this.http
      .get<IResponse<IServiceData>>(
        `${this.environment.apiGatewayBaseUrl}/service${apis.changeRequest.details}/${changeRequestId}/form-definition`
      )
      .pipe(
        map(res => {
          return res.data.fields;
        })
      );
  }
}
